import { Alert, Button, Chip, Grid, Link, Stack, Typography } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import { AvailableTicketsQuery, CampaignCategory } from '~/generated/graphql'
import { useMemo } from 'react'
import StoreIcon from '@mui/icons-material/Store'
import LocalShipping from '@mui/icons-material/LocalShipping'
import { pagesPath } from '~/lib/$path'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { colorPalette } from '~/constants/styles/colors'
import { getAvailableTicketAlert } from '~/lib/feature/getAvailableTicketAlert'

export type CategoryProps = {
  category: CampaignCategory | ''
  handleSelect: (event: SelectChangeEvent<string>) => void
  availableTickets?: (AvailableTicketsQuery['viewer']['company'] & { __typename: 'Company' })['availableTickets']
  ticketLoading: boolean
}

export const Category = ({ category, handleSelect, availableTickets, ticketLoading }: CategoryProps) => {
  const { availableTicketAlert } = useMemo(() => {
    return availableTickets && category !== ''
      ? getAvailableTicketAlert(category, availableTickets)
      : { availableTicketAlert: null }
  }, [availableTickets, category])

  return (
    <Stack>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CategoryButton
            selectedCategory={category}
            category={CampaignCategory.Service}
            label="店舗"
            description="インフルエンサーが現地に来店して体験するサービス"
            Icon={StoreIcon}
            onSelect={handleSelect}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <CategoryButton
            selectedCategory={category}
            category={CampaignCategory.Product}
            label="通販"
            description="インフルエンサーが自宅で体験するサービス"
            Icon={LocalShipping}
            onSelect={handleSelect}
          />
        </Grid>
      </Grid>

      {!ticketLoading && !!availableTicketAlert && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="caption">
            <span style={{ fontWeight: 'bold' }}>{availableTicketAlert}</span>
            のチケットがないためキャンペーンを審査に提出することができません。
          </Typography>
          <br />
          <Typography variant="caption">
            <Link
              href={pagesPath.contract.$url().pathname}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              チケットの購入・契約ページ
              <OpenInNewIcon sx={{ fontSize: 'inherit' }} />
            </Link>
            でチケットを購入してください。
          </Typography>
        </Alert>
      )}
    </Stack>
  )
}

type CategoryButtonProps = {
  selectedCategory: CampaignCategory | ''
  category: CampaignCategory
  label: string
  description: string
  Icon: React.ElementType
  onSelect: (event: SelectChangeEvent<string>) => void
}

const CategoryButton = ({ selectedCategory, category, label, description, Icon, onSelect }: CategoryButtonProps) => {
  const isSelected = selectedCategory === category

  return (
    <Button
      variant="outlined"
      fullWidth
      onClick={() => onSelect({ target: { name: 'category', value: category } } as SelectChangeEvent)}
      sx={{
        borderColor: colorPalette.other.outlineBorder,
        ...(isSelected && {
          backgroundColor: colorPalette.info.light,
        }),
        '&:hover': !isSelected ? { backgroundColor: colorPalette.action.hover } : undefined,
      }}
    >
      <Stack alignItems="center" gap={1} p={1} pb={2}>
        <Icon sx={{ fontSize: 80 }} color={isSelected ? 'primary' : 'disabled'} />
        <Chip color={isSelected ? 'primary' : 'default'} label={label} />
        <Typography variant="body2" sx={{ mt: 1 }} color={isSelected ? 'primary' : 'text.secondary'}>
          {description}
        </Typography>
      </Stack>
    </Button>
  )
}
